import { parseDatetimeLocal } from '../contexts/constants';

/**
 * Date Formatter Utility
 * จัดการ format วันที่ให้เป็นไปตามมาตรฐานของระบบ
 */

/**
 * แปลงวันที่เป็นรูปแบบไทย
 * @param {Date|string} date - วันที่ที่ต้องการแปลง
 * @param {string} format - รูปแบบที่ต้องการ ('short' | 'long' | 'datetime')
 * @returns {string} วันที่ในรูปแบบที่กำหนด
 */
export const formatThaiDate = (date, format = 'short', time) => {
	if (!date) return '';

	const dateObj = typeof date === 'string' ? parseDatetimeLocal(date) : date;

	// ตรวจสอบว่า date ถูกต้องหรือไม่
	if (isNaN(dateObj.getTime())) {
		console.warn('Invalid date:', date);
		return '';
	}

	const day = dateObj.getDate();
	const month = dateObj.getMonth() + 1; // เดือนเริ่มจาก 0
	const year = dateObj.getFullYear() + 543; // แปลงเป็นพุทธศักราช
	const hours = String(dateObj.getHours()).padStart(2, '0');
	const minutes = String(dateObj.getMinutes()).padStart(2, '0');

	if (format === 'short') {
		// รูปแบบ: "3/11/2568"
		return `${day}/${month}/${year}` + (time ? ` ${hours}:${minutes}` : '');
	} else if (format === 'short_text') {
		// รูปแบบ: "3 พฤศจิกายน 2568"
		const thaiMonths = [
			'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
			'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
		];
		return `${day} ${thaiMonths[dateObj.getMonth()]} ${year}` + (time ? ` ${hours}:${minutes}` : '');
	} else if (format === 'long') {
		// รูปแบบ: "3 พฤศจิกายน 2568"
		const thaiMonths = [
			'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
			'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
		];
		return `${day} ${thaiMonths[dateObj.getMonth()]} ${year}` + (time ? ` ${hours}:${minutes}` : '');
	} else if (format === 'datetime') {
		// รูปแบบ: "3/11/2568 14:30"
		return `${day}/${month}/${year} ${hours}:${minutes}`;
	}

	// ค่าเริ่มต้นถ้าไม่ระบุ format
	return `${day}/${month}/${year}`;
};

/**
 * แปลงวันที่เป็นรูปแบบไทยแบบสั้น (ค่าเริ่มต้นของระบบ)
 * @param {Date|string} date - วันที่ที่ต้องการแปลง
 * @returns {string} วันที่ในรูปแบบ "วัน/เดือน/ปี พ.ศ." เช่น "3/11/2568"
 */
export const formatDateShort = (date) => {
	return formatThaiDate(date, 'short');
};

/** * แปลงวันที่และเวลาเป็นรูปแบบไทยแบบสั้น
 * @param {Date|string} date - วันที่ที่ต้องการแปลง
 * @returns {string} วันที่และเวลาในรูปแบบ "วัน/เดือน/ปี พ.ศ. ชั่วโมง:นาที" เช่น "3/11/2568 14:30"
 */
export const formatDateTimeShort = (date) => {
	return formatThaiDate(date, 'short', true);
};

/**
 * แปลงวันที่เป็นรูปแบบไทยแบบยาว
 * @param {Date|string} date - วันที่ที่ต้องการแปลง
 * @returns {string} วันที่ในรูปแบบ "วัน เดือน ปี พ.ศ." เช่น "3 พฤศจิกายน 2568"
 */
export const formatDateLong = (date) => {
	return formatThaiDate(date, 'long');
};

/** * แปลงวันที่และเวลาเป็นรูปแบบไทยแบบยาว
 * @param {Date|string} date - วันที่ที่ต้องการแปลง
 * @returns {string} วันที่และเวลาในรูปแบบ "วัน เดือน ปี พ.ศ. ชั่วโมง:นาที" เช่น "3 พฤศจิกายน 2568 14:30"
 */
export const formatDateTimeLong = (date) => {
	return formatThaiDate(date, 'long', true);
};

/** * แปลงวันที่เป็นรูปแบบเริ่มต้นของระบบ (long)
 * @param {Date|string} date - วันที่ที่ต้องการแปลง
 * @returns {string} วันที่ในรูปแบบเริ่มต้นของระบบ
 */
export const formatDateDefault = (date) => {
	return formatDateLong(date);
};

/** * แปลงวันที่และเวลาเป็นรูปแบบเริ่มต้นของระบบ (long)
 * @param {Date|string} date - วันที่ที่ต้องการแปลง
 * @returns {string} วันที่ในรูปแบบเริ่มต้นของระบบ
 */
export const formatDateTimeDefault = (date) => {
	return formatDateTimeLong(date);
};

/** * แปลงวันที่เป็นรูปแบบตาราง (short)
 * @param {Date|string} date - วันที่ที่ต้องการแปลง
 * @returns {string} วันที่ในรูปแบบ "วัน/เดือน/ปี พ.ศ." เช่น "3/11/2568"
 */
export const formatDateTable = (date) => {
	return formatThaiDate(date, 'short_text', false);
};

/** * แปลงวันที่และเวลาเป็นรูปแบบตาราง (short)
 * @param {Date|string} date - วันที่ที่ต้องการแปลง
 * @returns {string} วันที่และเวลาในรูปแบบ "วัน/เดือน/ปี พ.ศ. ชั่วโมง:นาที" เช่น "3/11/2568 14:30"
 */
export const formatDateTimeTable = (date) => {
	return formatThaiDate(date, 'short_text', true);
};


/**
 * ดึงวันที่ปัจจุบันในรูปแบบไทย
 * @param {string} format - รูปแบบที่ต้องการ ('short' | 'long' | 'datetime')
 * @returns {string} วันที่ปัจจุบันในรูปแบบที่กำหนด
 */
export const getCurrentThaiDate = (format = 'short') => {
	return formatThaiDate(new Date(), format);
};

/**
 * แปลง Date object เป็น string สำหรับใช้กับ API (รูปแบบ "2025-01-01 11:23:21")
 * @param {Date} date - วันที่ที่ต้องการแปลง
 * @returns {string} วันที่ในรูปแบบ "YYYY-MM-DD HH:mm:ss"
 */
export const dateToAPI = (date) => {
	if (!date) return '';
	const dateObj = typeof date === 'string' ? parseDatetimeLocal(date) : date;

	const year = dateObj.getFullYear();
	const month = String(dateObj.getMonth() + 1).padStart(2, '0');
	const day = String(dateObj.getDate()).padStart(2, '0');
	const hours = String(dateObj.getHours()).padStart(2, '0');
	const minutes = String(dateObj.getMinutes()).padStart(2, '0');
	const seconds = String(dateObj.getSeconds()).padStart(2, '0');

	return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

/**
 * แปลง string จาก API เป็น Date object
 * @param {string} dateString - วันที่ในรูปแบบ "2025-01-01 11:23:21" จาก API
 * @returns {Date} Date object
 */
export const dateFromAPI = (dateString) => {
	if (!dateString) return null;
	return parseDatetimeLocal(dateString);
};

/**
 * ดึงวันที่และเวลาปัจจุบันในรูปแบบ API (รูปแบบ "2025-01-01 11:23:21")
 * @returns {string} วันที่และเวลาปัจจุบันในรูปแบบ "YYYY-MM-DD HH:mm:ss"
 */
export const getCurrentAPIDate = () => {
	return dateToAPI(new Date());
};

export const formatDateCountdown = (date) => {
	const dayNames = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];
	const monthNames = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
						'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
	const dateObj = typeof date === 'string' ? parseDatetimeLocal(date) : date;

	const dayName = dayNames[dateObj.getDay()];
	const day = dateObj.getDate();
	const month = monthNames[dateObj.getMonth()];
	const year = dateObj.getFullYear() + 543; // Convert to Buddhist year
	const time = dateObj.toLocaleTimeString('th-TH', {
		hour: '2-digit',
		minute: '2-digit',
		hour12: false
	});

	return `วัน${dayName}ที่ ${day} ${month} ${year} เวลา ${time} น.`;
};

export const formatDateFooter = (date) => {
	const dayNames = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];
	const monthNames = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
						'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
	const dateObj = typeof date === 'string' ? parseDatetimeLocal(date) : date;

	const dayName = dayNames[dateObj.getDay()];
	const day = dateObj.getDate();
	const month = monthNames[dateObj.getMonth()];
	const year = dateObj.getFullYear() + 543; // Convert to Buddhist year
	const time = dateObj.toLocaleTimeString('th-TH', {
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12: false
	});

	return `วัน${dayName} ที่ ${day} ${month} ${year} เวลา ${time}`;
};

export const formatDateFLot = (date) => {
	const dayNames = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์'];
	const monthNames = ['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
						'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
	const dateObj = typeof date === 'string' ? parseDatetimeLocal(date) : date;

	const dayName = dayNames[dateObj.getDay()];
	const day = dateObj.getDate();
	const month = monthNames[dateObj.getMonth()];
	const year = dateObj.getFullYear() + 543; // Convert to Buddhist year
	// const time = date.toLocaleTimeString('th-TH', {
	// 	hour: '2-digit',
	// 	minute: '2-digit',
	// 	hour12: false
	// });

	return `วัน${dayName} ที่ ${day} ${month} ${year}`;
};
